import React, { useEffect, useRef, useState } from 'react';
import { Button, Snackbar } from '@mui/material';
import { BUILD_VERSION } from '../utils/appVersion';

const POLL_INTERVAL_MS = 5 * 60 * 1000;

/**
 * Polls /version.json (never cached) and prompts reload when a new deploy is detected.
 * CRA already content-hashes JS/CSS; stale index.html is the usual cause of old bundles.
 */
const AppVersionChecker = () => {
  const [updateAvailable, setUpdateAvailable] = useState(false);
  const [remoteVersion, setRemoteVersion] = useState('');
  const dismissedRef = useRef(false);

  useEffect(() => {
    if (!BUILD_VERSION || BUILD_VERSION === 'dev') return undefined;

    const checkForUpdate = async () => {
      try {
        const response = await fetch(`/version.json?_=${Date.now()}`, {
          cache: 'no-store',
          headers: { 'Cache-Control': 'no-cache' },
        });
        if (!response.ok) return;

        const data = await response.json();
        if (
          data?.version
          && data.version !== BUILD_VERSION
          && data.version !== 'dev'
          && !dismissedRef.current
        ) {
          setRemoteVersion(data.version);
          setUpdateAvailable(true);
        }
      } catch {
        // Non-critical background check
      }
    };

    checkForUpdate();
    const intervalId = window.setInterval(checkForUpdate, POLL_INTERVAL_MS);

    const onVisible = () => {
      if (document.visibilityState === 'visible') {
        checkForUpdate();
      }
    };
    document.addEventListener('visibilitychange', onVisible);

    return () => {
      window.clearInterval(intervalId);
      document.removeEventListener('visibilitychange', onVisible);
    };
  }, []);

  const handleReload = () => {
    window.location.reload();
  };

  const handleDismiss = () => {
    dismissedRef.current = true;
    setUpdateAvailable(false);
  };

  return (
    <Snackbar
      open={updateAvailable}
      message={`A new version is available (${remoteVersion}). You are on ${BUILD_VERSION}.`}
      action={(
        <>
          <Button color="inherit" size="small" onClick={handleReload}>
            Reload now
          </Button>
          <Button color="inherit" size="small" onClick={handleDismiss}>
            Later
          </Button>
        </>
      )}
      anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
    />
  );
};

export default AppVersionChecker;
